import { Dispatch } from "redux";
import { User } from "../types/userTypes";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  getDocs,
  DocumentData,
  limit,
  startAfter,
  startAt,
  doc,
  getDoc,
  where,
  CollectionReference,
  QuerySnapshot,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import bcrypt from "bcryptjs";
import { verify } from "crypto";
import { sendEmail, validatePasswordChangeLimit } from "../../emailHandler";
import {
  currentTimestamp,
  generateHashPassword,
  generateRandomString,
  getDifferenceInMinutesBetweenTimeStamps,
  sendConfirmationEmail,
  sendWelcomeEmail,
} from "../../utils";
import { markNotificationsAsViewedByUserId } from "./notificationActions";

export const FETCH_USERS_REQUEST = "FETCH_USERS_REQUEST";
export const FETCH_USERS_SUCCESS = "FETCH_USERS_SUCCESS";
export const FETCH_USERS_FAILURE = "FETCH_USERS_FAILURE";
export const FETCH_USERS_RESET = "FETCH_USERS_RESET";

export const fetchUsersRequest = () => ({
  type: FETCH_USERS_REQUEST,
});

export const fetchUsersSuccess = (users: User[]) => ({
  type: FETCH_USERS_SUCCESS,
  payload: users,
});

export const fetchUsersFailure = (error: string) => ({
  type: FETCH_USERS_FAILURE,
  payload: error,
});

export const resetUserState = () => ({
  type: FETCH_USERS_RESET,
  //payload: users,
});

export const fetchUserByEmailAndPassword =
  (
    email: string,
    password: string,
    validateRecaptchaToken = "",
    limitLoginAttempts = false
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      if (validateRecaptchaToken) {
        const isCaptchaValid = await verifyRecaptchaToken(
          validateRecaptchaToken
        );
        if (!isCaptchaValid) {
          throw new Error("Captcha validation failed");
        }
      }
      const users = await getUniqueUserByEmailAndPassword(
        email,
        password,
        limitLoginAttempts
      );
      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const createUser =
  (email: string, password: string, validateRecaptchaToken = "") =>
  async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    const hashPassword = await generateHashPassword(password);
    try {
      if (validateRecaptchaToken) {
        const isCaptchaValid = await verifyRecaptchaToken(
          validateRecaptchaToken
        );
        if (!isCaptchaValid) {
          throw new Error("Captcha validation failed");
        }
      }
      const existingUsers = await getUniqueUserByEmail(email);
      if (existingUsers && existingUsers.length) {
        throw new Error("User already exists");
      }

      const createdTime = Date.now();
      // Add a new document with a generated id
      const docRef = await addDoc(collection(db, "users"), {
        createdTime: createdTime,
        email: email,
        isVerified: false,
        name: email,
        password: hashPassword,
        stripeid: "",
        loginAttempts: 0,
      });
      // Create a notification for the user to confirm account
      await addDoc(collection(db, "notifications"), {
        createdTime: createdTime,
        isViewed: false,
        title: "⚠️ Confirm Your Account",
        message:
          "Check your email and click 'Confirm' within 48 hours to activate your account, or it will be deleted. Click here to resend it",
        userId: createdTime,
      });
      // Retrieve the document
      const docSnap = await getDoc(docRef);
      const users = [docSnap.data()] as User[];
      await sendWelcomeEmail(email, password);
      await sendConfirmationEmail(email, createdTime);
      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error creating items."));
    }
  };

export const fetchUserByUniqueId =
  (id: number) => async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      const users: User[] = [];

      /* Query the "users" collection for a document with the specified productUrl. */
      let q = query(collection(db, "users"), where("createdTime", "==", id));

      let docData: User | undefined;
      const querySnapshot = await getDocs(q);
      for (const doc of querySnapshot.docs) {
        docData = doc.data() as User;
        docData.documentId = doc.id;
        users.push(docData);
      }

      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const confirmUserAccount =
  (id: number, email: string) => async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      let users: User[] = [];

      users = await getUniqueUserByEmail(email);
      if (users[0].createdTime === id && !users[0].isVerified) {
        await updateUserById(users[0], { isVerified: true });
        // Auto-clear "Confirm Your Account" notification (userId === createdTime)
        await markNotificationsAsViewedByUserId(id);
      } else {
        users = [];
      }

      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const fetchUserByEmail =
  (email: string) => async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      const users = await getUniqueUserByEmail(email);

      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const updateUserPassword =
  (email: string, currentPassword?: string, newPassword?: string) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      let users: User[] = [];
      users = await getUniqueUserByEmail(email);
      if (users && users.length) {
        let newNormalPassword;
        if (newPassword) {
          // it means password changing from settings so we need to validate currentPassword
          if (currentPassword) {
            const user = await getUniqueUserByEmailAndPassword(
              email,
              currentPassword
            );
            if (user && user.length) {
              newNormalPassword = newPassword;
            } else {
              throw new Error("Current Password or email is incorrect");
            }
          }
        } else {
          newNormalPassword = generateRandomString(8);
        }
        if (newNormalPassword) {
          const newHashPassword = await generateHashPassword(newNormalPassword);
          if (currentPassword && newPassword) {
            // it means password changing from settings so we need to validate limit only
            await validatePasswordChangeLimit(
              users[0].email,
              "Password Updated",
              newNormalPassword,
              "changePassword",
              { newPassword: newNormalPassword }
            );
          } else {
            await sendEmail(
              users[0].email,
              "Forgot Your Password?",
              newNormalPassword,
              "fpwEmail",
              { newPassword: newNormalPassword }
            );
          }
          await updateUserById(users[0], {
            password: newHashPassword,
            loginAttempts: 0,
          });
        }
      }
      dispatch(fetchUsersSuccess(users));
    } catch (error: any) {
      console.error(error);
      dispatch(fetchUsersFailure(String(error.message)));
    }
  };

const getUniqueUserByEmailAndPassword = async (
  email: string,
  password: string,
  limitLoginAttempts = false
) => {
  try {
    const users: User[] = [];

    /* Query the "users" collection for a document with the specified productUrl. */
    let q = query(
      collection(db, "users"),
      where("email", "==", email)
      //where("password", "==", password)
    );

    let docData: User | undefined;
    const querySnapshot = await getDocs(q);
    for (const doc of querySnapshot.docs) {
      docData = doc.data() as User;
      docData.documentId = doc.id;
      const match = await bcrypt.compare(password, docData.password || "");
      if (match) {
        let remainingMinutes = 0;
        if (docData.lastWrongPasswordTime) {
          remainingMinutes = getDifferenceInMinutesBetweenTimeStamps(
            currentTimestamp(),
            docData.lastWrongPasswordTime
          );
        }
        if (
          limitLoginAttempts &&
          (docData.loginAttempts < 4 || remainingMinutes > 30)
        ) {
          await updateUserById(docData, {
            loginAttempts: 0,
          });
          docData.loginAttempts = 0;
        }
        users.push(docData);
      } else {
        if (limitLoginAttempts) {
          const newLoginAttempts =
            docData.loginAttempts < 4
              ? docData.loginAttempts + 1
              : docData.loginAttempts;
          await updateUserById(docData, {
            loginAttempts: newLoginAttempts,
            lastWrongPasswordTime: currentTimestamp(),
          });
          if (docData.loginAttempts === 4) {
            users.push(docData);
          }
        }
      }
    }
    return users;
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching users.");
  }
};

const getUniqueUserByEmail = async (email: string) => {
  try {
    const users: User[] = [];

    /* Query the "users" collection for a document with the specified productUrl. */
    let q = query(collection(db, "users"), where("email", "==", email));

    let docData: User | undefined;
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      docData = doc.data() as User;
      docData.documentId = doc.id;
      users.push(docData);
    });
    return users;
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching users.");
  }
};

const verifyRecaptchaToken = async (token: string) => {
  try {
    // Send token to Google for verification
    const googleVerifyUrl = `https://www.google.com/recaptcha/api/siteverify?secret=${process.env.REACT_APP_CAPTCHA_SECRET_KEY}&response=${token}`;
    const response = await fetch(googleVerifyUrl, {
      method: "POST",
    });

    const verificationResult = await response.json();

    if (verificationResult.success) {
      // reCAPTCHA is valid
      return true;
    } else {
      // reCAPTCHA is invalid
      return false;
    }
  } catch (error) {
    console.error("Error during reCAPTCHA verification:", error);
    return false;
  }
};

const updateUserById = async (user: User, dataToUpdate: any) => {
  try {
    const docRef = doc(db, "users", user.documentId || "");
    await updateDoc(docRef, dataToUpdate);
  } catch (error) {
    console.error("Error updating document: ", error);
  }
};
